import MessageConst from './message-const';
import SystemConst from './system-const';
import { TableFilters } from '@/components/common/common-table-filter.component';
import { DataOptions } from 'vuetify';
import { FilterApiValue } from './type';

export const strictEntries = <T extends Record<string, any>>(
  object: T,
): [keyof T, T[keyof T]][] => {
  return Object.entries(object);
};

/**
 * 共通関数定義
 */
export default class CommonFunction {
  /**
   * 複数置換リプレース関数
   * @param source 置換元
   * @param substitutions 置換文字配列
   */
  public static replaceArray(source: string, substitutions: any[]): string {
    // 置換文字配列分繰り返しreplaceを実行する。
    substitutions.forEach((val, key) => {
      source = source.replace('{' + key + '}', val);
    });
    return source;
  }

  /**
   * ファイル拡張子取得関数
   */
  public static getExtension(filename: string): string {
    const index = filename.lastIndexOf('.');
    if (index === -1) {
      return '';
    }
    return filename.slice(index + 1);
  }

  /**
   * 最大桁数チェック
   * @param inputLength 入力された桁数
   * @param maxLength 最大桁数
   * @param displayText エラーに表示する項目名
   * @returns string 異常値ならエラーメッセージ
   *          boolean 正常値ならtrue
   */
  public static checkLength(
    inputLength: string,
    maxLength: number,
    displayText: string,
  ): string | boolean {
    // 値が未入力なら正常値としてリターンする
    if (!inputLength) {
      return true;
    }

    // 最大桁数を超えているかチェック
    if (inputLength.length > maxLength) {
      return CommonFunction.replaceArray(MessageConst.E_MAX, [
        displayText,
        maxLength,
      ]);
    }

    return true;
  }

  /**
   * 桁数範囲チェック
   * @param inputLength 入力された文字列
   * @param minLength 最小桁数
   * @param maxLength 最大桁数
   * @param displayText エラーに表示する項目名
   * @returns string 異常値ならエラーメッセージ
   *          boolean 正常値ならtrue
   */
  public static checkLengthRange(
    inputLength: string,
    minLength: number,
    maxLength: number,
    displayText: string,
  ): string | boolean {
    // 値が未入力なら正常値としてリターンする
    if (!inputLength) {
      return true;
    }

    // 最小桁数に足りていないか、最大桁数を超えていないかチェック
    if (inputLength.length < minLength || inputLength.length > maxLength) {
      return CommonFunction.replaceArray(MessageConst.E_MIN_MAX, [
        displayText,
        minLength,
        maxLength,
      ]);
    }

    return true;
  }

  /**
   * 一覧画面の検索パラメータ作成
   *
   * @param options ソート条件
   * @param filters フィルタ条件
   * @returns バック側へ渡すパラメータ
   * {
   *   'page': ページ番号
   *   'itemsPerPage': １ページあたりの行数
   *   'sortKey': ソートキー（v-data-tableの項目名)のカンマ区切り
   *   'sortOrder': ソート順('asc' or 'desc')
   *   'filterParams': {
   *                      '項目名1（v-data-tableの項目名)': {
   *                         'filterType': フィルタの種類('KeyWordPerfectMatch'等)
   *                         'filterValue': 検索値
   *                      },
   *                      '項目名2（v-data-tableの項目名)': {
   *                         'filterType': フィルタの種類('KeyWordPerfectMatch'等)
   *                         'filterValue': 検索値
   *                      }, ...
   *                   }
   *   }
   */
  public static makeListParameters(
    options: DataOptions,
    filters: TableFilters,
  ) {
    const params: { [key: string]: any } = {};
    const filterParams: { [key: string]: FilterApiValue } = {};

    // ページ情報
    if (options) {
      // 表示するページ番号
      params[SystemConst.LIST_API_PARAM.PAGE] = String(options.page);
      // １ページあたりの行数
      params[SystemConst.LIST_API_PARAM.ITEMS_PER_PAGE] = String(
        options.itemsPerPage,
      );
      // ソートキー（v-data-tableのheadersに設定した'value'）
      params[SystemConst.LIST_API_PARAM.SORT_KEY] = options.sortBy.join(',');
      // ソート順（false => 'asc' true => 'desc'へ変換）
      params[SystemConst.LIST_API_PARAM.SORT_ORDER] = options.sortDesc
        .map((e) => SystemConst.SORT_ORDER_MAPPING[Number(e)])
        .join(',');
    }

    // フィルタ条件
    //  以下の形式のJSONを作り、'filterParams'へ設定する。
    // {
    //     'ソート項目1': {
    //         'filterType': 'KeyWordPerfectMatch',
    //         'filterValue': 'フィルタ内容'
    //     },
    //     'ソート項目2': {
    //         'filterType': 'KeyWordPerfectMatch',
    //         'filterValue': 'フィルタ内容'
    //     }, ...
    // }
    //
    if (filters) {
      Object.keys(filters)
        .filter((key) => filters[key].isEnabled)
        .forEach((key) => {
          const e = filters[key];

          // 条件の種類によりパラメータの生成方法を変える
          filterParams[e.id] = { filterType: e.types, filterValue: '' };
          switch (e.types) {
            // 文字列検索 パラメータ名=keyword
            case 'KeyWordPerfectMatch':
              filterParams[e.id].filterValue = e.condition.keyword;
              break;
            case 'KeyWordPartialMatch':
              filterParams[e.id].filterValue = e.condition.keyword;
              break;
            // 範囲指定 パラメータ名=YYYY-MM-DD,YYYY-MM-DD
            case 'DatePeriod':
              filterParams[e.id].filterValue =
                String(e.condition.startDate || '') +
                ',' +
                String(e.condition.endDate || '');
              break;
            // 範囲指定 パラメータ名=X,X
            case 'NumRange':
              filterParams[e.id].filterValue =
                String(e.condition.minNumber || '') +
                ',' +
                String(e.condition.maxNumber || '');
              break;
            // 択一選択 パラメータ名=value
            case 'PullDown':
              filterParams[e.id].filterValue = e.condition.oneItem.value;
              break;
            // 複数選択 パラメータ名=value1,value2,...
            case 'PullDownMulti':
              filterParams[e.id].filterValue = e.condition.multiItem
                .map((v) => v.value)
                .join(',');
              break;
            // 複数選択 パラメータ名=value1,value2,...
            case 'CheckBox':
              filterParams[e.id].filterValue = e.condition.chkItems
                .filter((f) => f.isChecked)
                .map((chk) => chk.value)
                .join(',');
              break;
          }
        });
      params['filterParams'] = filterParams;
    }

    return params;
  }

  /**
   * プルダウン未選択チェック
   *
   * プルダウンが選択されていることをチェックする
   *
   * @param selectData    プルダウン
   * @param message       表示メッセージ
   * @returns string      異常値ならエラーメッセージ
   *          boolean     正常値ならtrue
   */
  public static checkPulldownData(
    selectData: any,
    message: string,
  ): string | boolean {
    // クリア、未選択の場合
    if (!selectData || !selectData.text) {
      return CommonFunction.replaceArray(MessageConst.E_UNSELECTED, [message]);
    }

    return true;
  }

  /**
   * Content-dispositionからファイル名を取得する
   *
   * HTTPヘッダcontent-dispositionから、ファイル名を取得する。
   * filename= と filename*=がある場合、filename*=を取得する。
   * filename=のみの場合、filename=を取得する。
   *
   * @param disposition content-dispositionの内容
   * @returns ファイル名
   */
  public static getFileNameFromContentDisposition(disposition: string) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/g;
    const matches = disposition.match(filenameRegex);

    if (matches !== null) {
      if (matches[1]) {
        const fileName = decodeURI(
          matches[1]
            .replace('filename=', '')
            .replace('filename*=', '')
            .replace(/['"]/g, '')
            .replace('utf-8', ''),
        );
        return fileName;
      } else {
        const fileName = decodeURI(
          matches[0]
            .replace('filename=', '')
            .replace('filename*=', '')
            .replace(/['"]/g, '')
            .replace('utf-8', ''),
        );
        return fileName;
      }
    } else {
      // ファイル名が無い場合
      return '';
    }
  }
}
